import { promiseTimeout } from '@iotize/common/promise';
import { TaskQueue } from '@iotize/common/task-manager';
import {
  ComProtocol,
  ComProtocolConnectOptions,
  ComProtocolDisconnectOptions,
  ComProtocolSendOptions,
  ConnectionState,
} from '@iotize/tap/protocol/api';
import { defer, Observable, of, Subscription, throwError } from 'rxjs';

import { finalize, share, switchMap, tap, timeoutWith } from 'rxjs/operators';
import { AbstractComProtocol } from './abstract-com-protocol';
import { debug } from './debug';

const TAG = 'ComProtocol';

/**
 *
 */
export abstract class QueueComProtocol extends AbstractComProtocol {
  _disconnect$?: Observable<any>;
  _connect$?: Observable<any>;
  _connectionSubscription?: Subscription;

  private _jobQueue: TaskQueue;

  abstract _connect(options?: ComProtocolConnectOptions): Observable<any>;

  abstract _disconnect(options?: ComProtocolDisconnectOptions): Observable<any>;

  public _send(
    data: Uint8Array,
    options: ComProtocolSendOptions,
  ): Observable<Uint8Array> {
    return defer(async () => {
      if (!this.isConnected()) {
        throw ComProtocol.Errors.notConnected({
          protocol: this,
        });
      }
      let startTime: Date = new Date();
      await promiseTimeout(options.timeout, this.write(data), (info) => {
        return ComProtocol.Errors.timeoutError({
          msg: `Communication timeout. Send request to device device timeout (${info.timeout}ms).`,
          protocol: this,
          timeout: info.timeout,
          startTime: info.startTime,
        });
      });
      let elapsedTime = new Date().getTime() - startTime.getTime();
      let newTimeout = options.timeout - elapsedTime;
      const result = await promiseTimeout(newTimeout, this.read(), (info) => {
        return ComProtocol.Errors.timeoutError({
          msg: `Communication timeout. Read response from device timeout (${newTimeout}ms).`,
          protocol: this,
          timeout: newTimeout,
          startTime: info.startTime,
        });
      });
      return result;
    }).pipe(share());
  }

  constructor() {
    super();
    this._jobQueue = new TaskQueue();
  }

  /**
   * Cancel pending requests
   */
  public cancel() {
    debug(TAG, `Canceling operations`);
    this._jobQueue.cancelAll(ComProtocol.Errors.operationCanceled());
  }

  /**
   *
   * @param data
   * @param options
   */
  public send(
    data: Uint8Array,
    options?: ComProtocolSendOptions,
  ): Observable<Uint8Array> {
    // debug(TAG, `QueueComProtocol`, `::send() ENQUEUE request (${data.length} bytes) ${toHexString(data)}. Queue position: ${this._queue.size}`);

    if (true) {
      // Copy data ? TODO can be removed ?
      data = data.slice(0);
    }

    return this._jobQueue.addExecutor(() => {
      let timeout = options ? options.timeout : this._options.send.timeout;
      return this._send(data, {
        timeout: timeout,
      });
    });
  }

  /**
   * Connect with timeout
   *
   * If connect has already been called or is in progress / no further action
   *
   * @param options
   */
  public connect(options?: ComProtocolConnectOptions): Observable<any> {
    if (this.connectionState === ConnectionState.CONNECTED) {
      debug(TAG, `QueueComProtocol is already connected`);
      return of(null);
    }
    if (this._connect$) {
      debug(TAG, `QueueComProtocol is already connecting...`);
      return this._connect$;
    }
    if (this._disconnect$) {
      return throwError(
        ComProtocol.Errors.operationInProgress(
          `Failed to connect as disconnection is in progress`,
        ),
      );
    }
    let connectTimeout = options
      ? options.timeout
      : this._options.connect.timeout;

    this._connect$ = defer(() => {
      this.setConnectionState(ConnectionState.CONNECTING);
      let startTime: Date = new Date();
      return this._connect(options).pipe(
        timeoutWith(
          connectTimeout,
          throwError(
            ComProtocol.Errors.timeoutError({
              msg: 'Protocol connection timeout',
              protocol: this,
              timeout: connectTimeout,
              startTime: startTime,
            }),
          ),
        ),
      );
    }).pipe(
      tap({
        complete: () => {
          this.setConnectionState(ConnectionState.CONNECTED);
        },
        error: () => {
          this.setConnectionState(ConnectionState.DISCONNECTED);
        },
      }),
      finalize(() => {
        this._connect$ = undefined;
      }),
      share(),
    );
    return this._connect$!;
  }

  public disconnect(options?: ComProtocolDisconnectOptions): Observable<any> {
    if (this.connectionState === ConnectionState.DISCONNECTED) {
      debug(TAG, `QueueComProtocol is already disconnected`);
      return of();
    }
    if (this._disconnect$) {
      return this._disconnect$;
    }
    if (this._connect$) {
      return throwError(
        ComProtocol.Errors.operationInProgress(
          `Failed to disconnect as connection is in progress`,
        ),
      );
    }
    let disconnectTimeout = options
      ? options.timeout
      : this._options.disconnect.timeout;

    this._disconnect$ = defer(async () => {
      this.setConnectionState(ConnectionState.DISCONNECTING);
    }).pipe(
      switchMap(() => {
        let startTime: Date = new Date();
        return this._disconnect(options).pipe(
          timeoutWith(
            disconnectTimeout,
            throwError(
              ComProtocol.Errors.timeoutError({
                msg: 'Protocol disconnection timeout',
                protocol: this,
                timeout: disconnectTimeout,
                startTime: startTime,
              }),
            ),
          ),
          finalize(() => {
            this.setConnectionState(ConnectionState.DISCONNECTED);
            this._disconnect$ = undefined;
          }),
        );
      }),
      share(),
    );

    return this._disconnect$;
  }
}
